"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import type { UserProfile } from "../../../api/types";
import { UsersAPI } from "../../../api/usersApi";

type UserProfilesContextValue = {
  profilesById: Record<string, UserProfile>;
  get: (userId: string) => UserProfile | undefined;
  ensureOne: (userId: string) => Promise<UserProfile | null>;
  ensure: (userIds: string[]) => Promise<void>;
  refreshOne: (userId: string) => Promise<UserProfile | null>;
};

const UserProfilesContext = createContext<UserProfilesContextValue | null>(
  null
);

/** A missing or failed id is not asked for again for this long. */
const ERROR_TTL_MS = 30_000;
/** A cached profile is refetched after this long. */
export const PROFILE_TTL_MS = 10 * 60_000;
/** The backend's `MAX_PROFILE_BATCH`. */
export const PROFILE_BATCH_SIZE = 100;

const compactIds = (ids: string[]) =>
  Array.from(
    new Set(
      ids.map((id) => (typeof id === "string" ? id.trim() : "")).filter(Boolean)
    )
  );

const nextFrame = (run: () => void) => {
  if (typeof window !== "undefined" && window.requestAnimationFrame) {
    window.requestAnimationFrame(run);
  } else {
    setTimeout(run, 0);
  }
};

const chunk = <T,>(items: T[], size: number) =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, i) =>
    items.slice(i * size, (i + 1) * size)
  );

export const useUserProfiles = () => {
  const ctx = useContext(UserProfilesContext);
  if (!ctx) {
    throw new Error(
      "useUserProfiles must be used within a UserProfilesProvider"
    );
  }
  return ctx;
};

/** The profiles cache, or `null` outside a provider. */
export const useOptionalUserProfiles = () => useContext(UserProfilesContext);

export const UserProfilesProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [profilesById, setProfilesById] = useState<Record<string, UserProfile>>(
    {}
  );
  const profilesRef = useRef<Record<string, UserProfile>>({});
  const fetchedAtRef = useRef<Record<string, number>>({});
  const erroredAtRef = useRef<Record<string, number>>({});
  const inFlightRef = useRef<Map<string, Promise<UserProfile | null>>>(
    new Map()
  );
  const queuedRef = useRef<Map<string, (p: UserProfile | null) => void>>(
    new Map()
  );
  const flushScheduled = useRef(false);

  useEffect(() => {
    profilesRef.current = profilesById;
  }, [profilesById]);

  const get = useCallback(
    (userId: string) => {
      const id = (userId ?? "").trim();
      if (!id) return undefined;
      return profilesById[id];
    },
    [profilesById]
  );

  const flush = useCallback(async () => {
    flushScheduled.current = false;
    const queued = new Map(queuedRef.current);
    queuedRef.current.clear();
    await Promise.all(
      chunk(Array.from(queued.keys()), PROFILE_BATCH_SIZE).map(async (ids) => {
        let found: Record<string, UserProfile> = {};
        try {
          found = await UsersAPI.getProfiles(ids);
        } catch {
          found = {};
        }
        const now = Date.now();
        const hits = ids.filter((id) => found[id]);
        if (hits.length) {
          setProfilesById((prev) => {
            const next = { ...prev };
            for (const id of hits) next[id] = found[id];
            return next;
          });
        }
        for (const id of ids) {
          if (found[id]) {
            fetchedAtRef.current[id] = now;
            delete erroredAtRef.current[id];
            profilesRef.current = { ...profilesRef.current, [id]: found[id] };
          } else {
            erroredAtRef.current[id] = now;
          }
          inFlightRef.current.delete(id);
          queued.get(id)?.(found[id] ?? null);
        }
      })
    );
  }, []);

  const request = useCallback(
    (id: string, force: boolean): Promise<UserProfile | null> => {
      const now = Date.now();
      const cached = profilesRef.current[id];
      const fresh = now - (fetchedAtRef.current[id] ?? 0) < PROFILE_TTL_MS;
      if (!force && cached && fresh) return Promise.resolve(cached);
      const erroredAt = erroredAtRef.current[id];
      if (!force && erroredAt && now - erroredAt < ERROR_TTL_MS) {
        return Promise.resolve(cached ?? null);
      }
      const inFlight = inFlightRef.current.get(id);
      if (inFlight) return inFlight;

      const promise = new Promise<UserProfile | null>((resolve) => {
        queuedRef.current.set(id, resolve);
      });
      inFlightRef.current.set(id, promise);
      if (!flushScheduled.current) {
        flushScheduled.current = true;
        nextFrame(() => void flush());
      }
      return promise;
    },
    [flush]
  );

  const ensureOne = useCallback(
    async (userId: string) => {
      const id = (userId ?? "").trim();
      return id ? await request(id, false) : null;
    },
    [request]
  );

  const ensure = useCallback(
    async (userIds: string[]) => {
      await Promise.all(compactIds(userIds).map((id) => request(id, false)));
    },
    [request]
  );

  const refreshOne = useCallback(
    async (userId: string) => {
      const id = (userId ?? "").trim();
      return id ? await request(id, true) : null;
    },
    [request]
  );

  const value = useMemo<UserProfilesContextValue>(() => {
    return { profilesById, get, ensureOne, ensure, refreshOne };
  }, [profilesById, get, ensureOne, ensure, refreshOne]);

  return (
    <UserProfilesContext.Provider value={value}>
      {children}
    </UserProfilesContext.Provider>
  );
};
